import React, { useState } from 'react'

import { Box, Typography, makeStyles, Theme } from '@material-ui/core'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'

import { EzosLoader } from '../../../components'
import {
    MARKETING_SOURCE_COLOR,
    MARKETING_SOURCE_LABEL,
    MARKETING_STAGE_COLUMNS,
    MarketingCampaignRow,
    MarketingSourceRow,
    MarketingStageCounts,
} from './types'
import { useMarketingChannels } from './hooks/useMarketingChannels'

const STATUS_LABEL: Record<string, string> = {
    DRAFT: 'Draft',
    ACTIVE: 'Active',
    PAUSED: 'Paused',
    ARCHIVED: 'Archived',
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    tiles: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: theme.spacing(2),
    },
    tile: {
        padding: theme.spacing(2.5, 3),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        backgroundColor: theme.palette.background.paper,
    },
    tileValue: {
        fontSize: '2.25rem',
        lineHeight: 1,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '-0.03em',
        color: theme.palette.text.primary,
    },
    tileLabel: {
        marginTop: theme.spacing(0.75),
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
    },
    chart: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
    },
    columns: {
        display: 'grid',
        gridTemplateColumns:
            'minmax(180px, 230px) minmax(100px, 1fr) 160px 160px',
        alignItems: 'center',
        columnGap: theme.spacing(3),
        width: '100%',
        padding: 0,
        border: 'none',
        background: 'none',
        textAlign: 'left',
        color: 'inherit',
        font: 'inherit',
    },
    columnHead: {
        padding: theme.spacing(1.75, 2.5, 1),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    columnLabel: {
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    sourceRow: {
        padding: theme.spacing(2, 2.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    rowButton: {
        cursor: 'pointer',
    },
    nameGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        minWidth: 0,
    },
    swatch: {
        width: 10,
        height: 10,
        borderRadius: '50%',
        flexShrink: 0,
    },
    name: {
        fontSize: '0.9375rem',
        fontWeight: 600,
    },
    metric: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 4,
        minWidth: 0,
    },
    metricValue: {
        fontSize: '1.25rem',
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1,
    },
    metricDetail: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
    },
    metricTrack: {
        width: '100%',
        height: 6,
        marginTop: 2,
        borderRadius: 999,
        backgroundColor: theme.palette.action.hover,
        overflow: 'hidden',
    },
    metricFill: {
        height: '100%',
        borderRadius: 999,
    },
    volumeTrack: {
        height: 10,
        borderRadius: 999,
        backgroundColor: theme.palette.action.hover,
        overflow: 'hidden',
    },
    volumeFill: {
        height: '100%',
        borderRadius: 999,
    },
    detail: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
        marginLeft: theme.spacing(2.75),
        paddingLeft: theme.spacing(2),
        borderLeft: `2px solid ${theme.palette.divider}`,
    },
    stageRow: {
        display: 'grid',
        gridTemplateColumns: '132px minmax(80px, 1fr) 36px',
        alignItems: 'center',
        columnGap: theme.spacing(1.5),
    },
    stageLabel: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
    },
    stageTrack: {
        height: 6,
        borderRadius: 999,
        backgroundColor: theme.palette.action.hover,
        overflow: 'hidden',
    },
    stageFill: {
        height: '100%',
        borderRadius: 999,
    },
    stageCount: {
        fontSize: '0.8125rem',
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
        textAlign: 'right',
    },
    campaignName: {
        fontSize: '0.875rem',
        fontWeight: 600,
        lineHeight: 1.3,
    },
    campaignStatus: {
        display: 'block',
        marginTop: 2,
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    emptyCampaigns: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
    },
    message: {
        padding: theme.spacing(5),
        textAlign: 'center',
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
    },
}))

function percentLabel(count: number, total: number): string {
    if (!total) return '—'
    return `${Math.round((count / total) * 100)}%`
}

function countOf(count: number, total: number, suffix?: string): string {
    const base = `${count.toLocaleString()} of ${total.toLocaleString()}`
    return suffix ? `${base} ${suffix}` : base
}

function rankSources(sources: MarketingSourceRow[]): MarketingSourceRow[] {
    const order = sources.map((source) => source.key)
    return [...sources].sort((a, b) => {
        if (b.total !== a.total) return b.total - a.total
        return order.indexOf(a.key) - order.indexOf(b.key)
    })
}

function rankCampaigns(
    campaigns: MarketingCampaignRow[],
): MarketingCampaignRow[] {
    return [...campaigns].sort((a, b) => {
        if (b.total !== a.total) return b.total - a.total
        return (a.name || '').localeCompare(b.name || '')
    })
}

function averageConversionRate(sources: MarketingSourceRow[]): number | null {
    const withLeads = sources.filter((source) => source.total > 0)
    if (withLeads.length === 0) return null
    const sum = withLeads.reduce(
        (total, source) => total + source.converted / source.total,
        0,
    )
    return sum / withLeads.length
}

function VolumeBar({
    total,
    max,
    color,
    label,
}: {
    total: number
    max: number
    color: string
    label: string
}) {
    const classes = useStyles()
    const width = max > 0 && total > 0 ? (total / max) * 100 : 0
    return (
        <Box
            className={classes.volumeTrack}
            role="img"
            aria-label={`${label}, ${total.toLocaleString()} leads`}
        >
            <Box
                className={classes.volumeFill}
                style={{
                    width: `${width}%`,
                    backgroundColor: color,
                }}
            />
        </Box>
    )
}

function StageBreakdown({
    stages,
    total,
    color,
}: {
    stages: MarketingStageCounts
    total: number
    color: string
}) {
    const classes = useStyles()
    return (
        <Box className={classes.detail}>
            {MARKETING_STAGE_COLUMNS.map((column) => {
                const count = stages[column.key] ?? 0
                const width = total > 0 ? (count / total) * 100 : 0
                return (
                    <Box key={column.key} className={classes.stageRow}>
                        <Typography className={classes.stageLabel}>
                            {column.label}
                        </Typography>
                        <Box className={classes.stageTrack} aria-hidden>
                            <Box
                                className={classes.stageFill}
                                style={{
                                    width: `${width}%`,
                                    backgroundColor: color,
                                }}
                            />
                        </Box>
                        <Typography className={classes.stageCount}>
                            {count.toLocaleString()}
                        </Typography>
                    </Box>
                )
            })}
        </Box>
    )
}

function Metric({
    value,
    detail,
    fill,
    color,
    label,
}: {
    value: string
    detail: string
    fill: number
    color: string
    label: string
}) {
    const classes = useStyles()
    const width = Math.max(0, Math.min(100, fill))
    return (
        <Box
            className={classes.metric}
            aria-label={`${label}: ${value}, ${detail}`}
        >
            <Typography className={classes.metricValue}>{value}</Typography>
            <Typography className={classes.metricDetail}>{detail}</Typography>
            <Box className={classes.metricTrack} aria-hidden>
                <Box
                    className={classes.metricFill}
                    style={{
                        width: `${width}%`,
                        backgroundColor: color,
                    }}
                />
            </Box>
        </Box>
    )
}

function Pair({
    leadCount,
    seasonTotal,
    converted,
    sourceColor,
}: {
    leadCount: number
    seasonTotal: number
    converted: number
    sourceColor: string
}) {
    const share = seasonTotal > 0 ? (leadCount / seasonTotal) * 100 : 0
    const rate = leadCount > 0 ? (converted / leadCount) * 100 : 0
    return (
        <>
            <Metric
                label="Lead source"
                value={percentLabel(leadCount, seasonTotal)}
                detail={
                    seasonTotal ? countOf(leadCount, seasonTotal) : 'No leads'
                }
                fill={share}
                color={sourceColor}
            />
            <Metric
                label="Conversion rate"
                value={percentLabel(converted, leadCount)}
                detail={
                    leadCount
                        ? countOf(converted, leadCount, 'signed')
                        : 'No leads'
                }
                fill={rate}
                color="#16a34a"
            />
        </>
    )
}

function CampaignRows({
    campaigns,
    seasonTotal,
    max,
    color,
}: {
    campaigns: MarketingCampaignRow[]
    seasonTotal: number
    max: number
    color: string
}) {
    const classes = useStyles()
    const [openIds, setOpenIds] = useState<Record<number, boolean>>({})
    if (campaigns.length === 0) {
        return (
            <Typography className={classes.emptyCampaigns}>
                No campaigns have produced leads.
            </Typography>
        )
    }
    return (
        <>
            {rankCampaigns(campaigns).map((campaign) => {
                const open = !!openIds[campaign.id]
                return (
                    <Box key={campaign.id}>
                        <button
                            type="button"
                            className={`${classes.columns} ${classes.rowButton}`}
                            aria-expanded={open}
                            onClick={() =>
                                setOpenIds((current) => ({
                                    ...current,
                                    [campaign.id]: !open,
                                }))
                            }
                        >
                            <Box className={classes.nameGroup}>
                                {open ? (
                                    <KeyboardArrowDownIcon fontSize="small" />
                                ) : (
                                    <KeyboardArrowRightIcon fontSize="small" />
                                )}
                                <Box>
                                    <Typography
                                        className={classes.campaignName}
                                    >
                                        {campaign.name}
                                    </Typography>
                                    <span className={classes.campaignStatus}>
                                        {STATUS_LABEL[campaign.status] ??
                                            campaign.status}
                                    </span>
                                </Box>
                            </Box>
                            <VolumeBar
                                total={campaign.total}
                                max={max}
                                color={color}
                                label={campaign.name}
                            />
                            <Pair
                                leadCount={campaign.total}
                                seasonTotal={seasonTotal}
                                converted={campaign.converted}
                                sourceColor={color}
                            />
                        </button>
                        {open && (
                            <StageBreakdown
                                stages={campaign.stages}
                                total={campaign.total}
                                color={color}
                            />
                        )}
                    </Box>
                )
            })}
        </>
    )
}

function SourceRow({
    source,
    seasonTotal,
    max,
    expanded,
    onToggle,
    campaigns,
}: {
    source: MarketingSourceRow
    seasonTotal: number
    max: number
    expanded: boolean
    onToggle: () => void
    campaigns: MarketingCampaignRow[]
}) {
    const classes = useStyles()
    const color = MARKETING_SOURCE_COLOR[source.key]
    const isCampaign = source.key === 'email'
    const identity = (
        <Box className={classes.nameGroup}>
            {expanded ? (
                <KeyboardArrowDownIcon fontSize="small" />
            ) : (
                <KeyboardArrowRightIcon fontSize="small" />
            )}
            <span
                className={classes.swatch}
                style={{ backgroundColor: color }}
            />
            <Typography className={classes.name}>
                {MARKETING_SOURCE_LABEL[source.key]}
            </Typography>
        </Box>
    )
    const metrics = (
        <Pair
            leadCount={source.total}
            seasonTotal={seasonTotal}
            converted={source.converted}
            sourceColor={color}
        />
    )

    return (
        <Box className={classes.sourceRow}>
            <button
                type="button"
                className={`${classes.columns} ${classes.rowButton}`}
                aria-expanded={expanded}
                onClick={onToggle}
            >
                {identity}
                <VolumeBar
                    total={source.total}
                    max={max}
                    color={color}
                    label={MARKETING_SOURCE_LABEL[source.key]}
                />
                {metrics}
            </button>
            {expanded &&
                (isCampaign ? (
                    <Box className={classes.detail}>
                        <CampaignRows
                            campaigns={campaigns}
                            seasonTotal={seasonTotal}
                            max={max}
                            color={color}
                        />
                    </Box>
                ) : (
                    <StageBreakdown
                        stages={source.stages}
                        total={source.total}
                        color={color}
                    />
                ))}
        </Box>
    )
}

interface MarketingChannelsHomeProps {
    seasonId?: number | null
}

export const MarketingChannelsHome: React.FC<MarketingChannelsHomeProps> = ({
    seasonId,
}) => {
    const classes = useStyles()
    const [openSources, setOpenSources] = useState<Record<string, boolean>>({})
    const query = useMarketingChannels(seasonId)

    if (seasonId == null) {
        return (
            <Typography className={classes.message}>
                Select a leasing season to see marketing channels.
            </Typography>
        )
    }

    if (query.isLoading && !query.data) {
        return <EzosLoader />
    }

    if (query.isError || !query.data) {
        return (
            <Typography className={classes.message}>
                Marketing channels could not be loaded.
            </Typography>
        )
    }

    const { total, sources, campaigns } = query.data
    const ranked = rankSources(sources)
    const max = Math.max(...ranked.map((source) => source.total), 1)
    const averageRate = averageConversionRate(ranked)

    return (
        <Box className={classes.root}>
            <Box className={classes.tiles}>
                <Box className={classes.tile}>
                    <Typography className={classes.tileValue}>
                        {total.toLocaleString()}
                    </Typography>
                    <Typography className={classes.tileLabel}>Leads</Typography>
                </Box>
                <Box className={classes.tile}>
                    <Typography className={classes.tileValue}>
                        {averageRate == null
                            ? '—'
                            : `${Math.round(averageRate * 100)}%`}
                    </Typography>
                    <Typography className={classes.tileLabel}>
                        Conversion rate
                    </Typography>
                </Box>
            </Box>
            <Box className={classes.chart}>
                <Box className={`${classes.columns} ${classes.columnHead}`}>
                    <span />
                    <span />
                    <Typography className={classes.columnLabel}>
                        Lead source
                    </Typography>
                    <Typography className={classes.columnLabel}>
                        Conversion rate
                    </Typography>
                </Box>
                {ranked.map((source) => (
                    <SourceRow
                        key={source.key}
                        source={source}
                        seasonTotal={total}
                        max={max}
                        expanded={!!openSources[source.key]}
                        onToggle={() =>
                            setOpenSources((current) => ({
                                ...current,
                                [source.key]: !current[source.key],
                            }))
                        }
                        campaigns={campaigns}
                    />
                ))}
            </Box>
        </Box>
    )
}
